import axios from 'axios'
import type { AxiosInstance, AxiosTransformer } from 'axios'
import { createApiClient } from '@clouway/api'
import type { ApiClient } from '@clouway/api'

import { reviveDates, serializeDates, serializeParams } from './dates'
import { NetworkError } from './errors'

export interface CreateClientOptions {
  baseURL?: string
  /** Invoked on a 401 response; defaults to a full-page /auth/login navigation. */
  onUnauthenticated?: () => void
  configure?: (instance: AxiosInstance) => void
}

// Guard so a burst of concurrent 401s (or a probe 401 racing the SessionProvider)
// triggers exactly one navigation.
let redirecting = false

function redirectToLogin() {
  if (redirecting) return
  // Never bounce while already on the auth flow — avoids a redirect loop.
  if (window.location.pathname.startsWith('/auth/')) return
  redirecting = true
  // Preserve the deep link: browserauth's LoginHandler round-trips a validated
  // return_to through the OAuth state, so the user lands back where they were.
  const returnTo = window.location.pathname + window.location.search
  window.location.assign(`/auth/login?return_to=${encodeURIComponent(returnTo)}`)
}

export function createClient(options: CreateClientOptions = {}): ApiClient {
  // /bff is the cookie-authenticated browser tree on the gateway — kept
  // separate from /api/v1 (fproxy/bearer ingress for the mobile app).
  const { baseURL = '/bff', onUnauthenticated = redirectToLogin, configure } = options

  return createApiClient({
    baseURL,
    configure: (instance) => {
      instance.defaults.paramsSerializer = serializeParams
      instance.defaults.transformRequest = [
        (data: unknown) => serializeDates(data),
        ...(axios.defaults.transformRequest as AxiosTransformer[]),
      ]
      instance.interceptors.response.use(
        (response) => {
          response.data = reviveDates(response.data)
          return response
        },
        (error: unknown) => {
          if (axios.isAxiosError(error)) {
            if (error.response === undefined) {
              return Promise.reject(new NetworkError(error.message || 'Network error', { cause: error }))
            }
            if (error.response.status === 401) {
              onUnauthenticated()
            }
          }
          return Promise.reject(error)
        },
      )
      configure?.(instance)
    },
  })
}

export const api = createClient()
